import React, { useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

interface IAnimatedNumberProps {
    count: number;
    duration?: number;
    prefix?: string;
    suffix?: string;
    compact?: boolean;          // abbreviate large numbers (1.2K, 3.4M)
    compactThreshold?: number;  // value at/above which compact kicks in
}

const formatFull = (value: number, prefix: string, suffix: string) =>
    `${prefix}${value.toLocaleString()}${suffix}`;

const formatCompact = (value: number, prefix: string, suffix: string) =>
    `${prefix}${new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1,
    }).format(value)}${suffix}`;

const AnimatedNumber = ({
    count,
    duration = 1200,
    prefix = '',
    suffix = '',
    compact = true,
    compactThreshold = 999999,
}: IAnimatedNumberProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLSpanElement>(null);
    const initialFontSizeRef = useRef<number | null>(null);
    const end = count;
    const [displayValue, setDisplayValue] = useState(0);

    const isCompactActive = compact && Math.abs(end) >= compactThreshold;

    // Animate the count up
    useEffect(() => {
        let start = 0;
        const isFloat = !Number.isInteger(end);
        const frameRate = 1000 / 60;
        const totalFrames = Math.max(1, Math.round(duration / frameRate));
        const increment = end / totalFrames;
        let frame = 0;

        const counter = setInterval(() => {
            frame++;
            start += increment;

            const current =
                frame >= totalFrames ? end : isFloat ? parseFloat(start.toFixed(1)) : Math.round(start);

            setDisplayValue(current);
            if (frame >= totalFrames) clearInterval(counter);
        }, frameRate);

        return () => clearInterval(counter);
    }, [end, duration]);

    // Shrink font-size only as much as needed to keep the full text on one line
    useEffect(() => {
        const container = containerRef.current;
        const text = textRef.current;
        if (!container || !text) return;

        const fit = () => {
            if (initialFontSizeRef.current === null) {
                initialFontSizeRef.current = parseFloat(getComputedStyle(text).fontSize);
            }
            const maxSize = initialFontSizeRef.current;
            const minSize = maxSize * 0.55; // don't shrink past ~55% of design size
            let size = maxSize;

            text.style.fontSize = `${size}px`;
            while (text.scrollWidth > container.clientWidth && size > minSize) {
                size -= 1;
                text.style.fontSize = `${size}px`;
            }
        };

        fit();
        const resizeObserver = new ResizeObserver(fit);
        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
    }, [displayValue, isCompactActive]);

    const shownText = isCompactActive
        ? formatCompact(displayValue, prefix, suffix)
        : formatFull(displayValue, prefix, suffix);

    const fullText = formatFull(end, prefix, suffix);

    const numberEl = (
        <div className="dash-overview-count" ref={containerRef}>
            <span ref={textRef}>{shownText}</span>
        </div>
    );

    // Only attach a tooltip when we're actually abbreviating something
    if (isCompactActive) {
        return (
            <OverlayTrigger placement="top" overlay={<Tooltip>{fullText}</Tooltip>}>
                {numberEl}
            </OverlayTrigger>
        );
    }

    return numberEl;
};

export default AnimatedNumber;